import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/LoginPage.css";
import logo from "../assets/logo.png";
import {
  loginUser,
  fetchAuthCapabilities,
  requestOtp,
  verifyOtp,
  selectOtpAccount,
} from "../services/api";

function EyeIcon({ open }) {
  return open ? (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.68 19.68 0 0 1 4.22-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a19.62 19.62 0 0 1-2.16 3.19M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [otpEnabled, setOtpEnabled] = useState(false);
  const [mode, setMode] = useState("password");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState("mobile");
  const [requestId, setRequestId] = useState("");
  const [selectionToken, setSelectionToken] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [resendAfter, setResendAfter] = useState(0);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchAuthCapabilities()
      .then((data) => {
        const enabled = data?.otpLoginEnabled === true;
        setOtpEnabled(enabled);
        if (enabled) setMode("otp");
      })
      .catch(() => {
        setOtpEnabled(false);
        setMode("password");
      });
  }, []);

  useEffect(() => {
    if (resendAfter <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendAfter((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendAfter]);

  const completeLogin = (response) => {
    if (!response?.accountName || !response?.sessionToken) {
      throw new Error("Unable to complete login");
    }

    onLogin({
      accountName: response.accountName,
      accountUniqueCode: response.accountUniqueCode,
      mobile: response.mobile,
      sessionToken: response.sessionToken,
    });
    navigate("/dashboard");
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await loginUser(username, password);
      completeLogin(response);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();

    if (!mobile.trim()) {
      setError("Please enter your registered mobile number.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await requestOtp(mobile);
      setRequestId(response.requestId);
      setOtp("");
      setOtpStep("code");
      setResendAfter(response.resendAfterSeconds || 60);
    } catch (err) {
      setError(err.message || "Unable to send OTP");
      if (err.retryAfter) setResendAfter(err.retryAfter);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(otp.trim())) {
      setError("Please enter the 6-digit OTP.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await verifyOtp(requestId, otp.trim());

      if (response.needsAccountSelection) {
        setSelectionToken(response.selectionToken);
        setAccounts(response.accounts || []);
        setOtpStep("account");
      } else {
        completeLogin(response);
      }
    } catch (err) {
      setError(err.message || "Unable to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccount = async (accountUniqueCode) => {
    try {
      setLoading(true);
      setError("");
      const response = await selectOtpAccount(selectionToken, accountUniqueCode);
      completeLogin(response);
    } catch (err) {
      setError(err.message || "Unable to complete login");
    } finally {
      setLoading(false);
    }
  };

  const resetOtp = () => {
    setOtpStep("mobile");
    setRequestId("");
    setOtp("");
    setSelectionToken("");
    setAccounts([]);
    setError("");
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-logo-wrapper">
            <img src={logo} alt="ABN Textiles Logo" className="login-logo" />
          </div>

          <h1 className="login-title">Dispatch Portal</h1>
          <p className="login-company">ABN Textiles (I) Pvt. Ltd.</p>
        </div>

        <p className="login-subtitle">Dealer Login</p>

        {otpEnabled && (
          <div className="login-mode-tabs">
            <button
              type="button"
              className={"login-mode-tab" + (mode === "otp" ? " active" : "")}
              onClick={() => {
                setMode("otp");
                setError("");
              }}
            >
              Login with OTP
            </button>
            <button
              type="button"
              className={"login-mode-tab" + (mode === "password" ? " active" : "")}
              onClick={() => {
                setMode("password");
                setError("");
              }}
            >
              Login with Password
            </button>
          </div>
        )}

        {mode === "otp" && otpEnabled ? (
          <>
            {otpStep === "mobile" && (
              <form className="login-form" onSubmit={handleRequestOtp}>
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="Registered mobile number"
                  className="login-input"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  autoComplete="tel"
                />

                <p className="login-help">
                  We will send a 6-digit verification code to your WhatsApp. After signing in, you can set or change your portal password in the dashboard.
                </p>

                {error && <p className="login-error">{error}</p>}

                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? "Sending..." : "Send OTP on WhatsApp"}
                </button>
              </form>
            )}

            {otpStep === "code" && (
              <form className="login-form" onSubmit={handleVerifyOtp}>
                <p className="login-help">
                  OTP sent to your registered WhatsApp number.
                </p>

                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit OTP"
                  className="login-input otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  autoComplete="one-time-code"
                />

                {error && <p className="login-error">{error}</p>}

                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? "Verifying..." : "Verify & Login"}
                </button>

                <div className="otp-actions">
                  <button type="button" className="login-link-button" onClick={resetOtp}>
                    Change mobile number
                  </button>
                  <button
                    type="button"
                    className="login-link-button"
                    disabled={loading || resendAfter > 0}
                    onClick={handleRequestOtp}
                  >
                    {resendAfter > 0 ? "Resend in " + resendAfter + "s" : "Resend OTP"}
                  </button>
                </div>
              </form>
            )}

            {otpStep === "account" && (
              <div className="login-form">
                <p className="login-help">
                  This mobile number is linked with more than one ABN account. Select the account you want to open.
                </p>

                <div className="account-choice-list">
                  {accounts.map((account) => (
                    <button
                      key={account.accountUniqueCode}
                      type="button"
                      className="account-choice-button"
                      disabled={loading}
                      onClick={() => handleSelectAccount(account.accountUniqueCode)}
                    >
                      {account.accountName}
                    </button>
                  ))}
                </div>

                {error && <p className="login-error">{error}</p>}

                <button type="button" className="login-link-button" onClick={resetOtp}>
                  Start again
                </button>
              </div>
            )}
          </>
        ) : (
          <form className="login-form" onSubmit={handlePasswordLogin}>
            <input
              type="text"
              placeholder="Registered mobile number"
              className="login-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />

            <div className="password-field-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter Password"
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle-button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>

            {error && <p className="login-error">{error}</p>}

            {otpEnabled && (
              <button
                type="button"
                className="login-link-button"
                onClick={() => {
                  setMobile(username);
                  setOtpStep("mobile");
                  setRequestId("");
                  setError("");
                  setMode("otp");
                }}
              >
                Forgot password? Sign in with OTP to reset it
              </button>
            )}

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
